import './ReceptionForm.scss';

import { Alert, Form, Tag } from 'antd';
import { FormProps } from 'antd/lib';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import FormRecordHeader from '@/components/general/FormRecordHeader';
import FormSection from '@/components/general/FormSection';
// Mejora: se reemplazó ReceptionReturnAddressForm por el componente genérico LogisticAddressFormSection
import LogisticAddressFormSection from '@/components/logistic/LogisticAddressFormSection';
import PackageItemsList from '@/components/logistic/PackageItems/PackageItemsList';
import PackageStateTag from '@/components/logistic/PackageStateTag';
import { COUNTRY, useCountryContext } from '@/contexts/CountryContext';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { RECEPTION_TYPE } from '@/model/Logistic/Reception/ReceptionType';
// Mejora: se reemplazó el Modal.error manual y onFinishFailed inline por helpers genéricos
// (showContactWarningModal, createFinishFailedHandler) para eliminar duplicidad con ShipmentForm.
// Se reemplazó el header manual de edición por FormRecordHeader y las Cards repetidas por FormSection.
import { createFinishFailedHandler, showContactWarningModal } from '@/utils/formHelpers';

import ReceptionContactForm from './ReceptionContactForm';
import ReceptionDetailForm from './ReceptionDetailForm';
import ReceptionLogisticForm from './ReceptionLogisticForm';
import { useReceptionForm } from './useReceptionForm';

interface ReceptionFormProps {
  formProps: FormProps;
  edit?: boolean;
  packageItems: PackageItem[];
  setPackageItems: (items: PackageItem[]) => void;
  setSaveAddress?: (save: boolean) => void;
}

const ReceptionForm = (props: ReceptionFormProps) => {
  const { isBrasilSelected } = useCountryContext();
  const { t } = useTranslation();
  const { formProps, edit, packageItems, setPackageItems, setSaveAddress } = props;
  const { initialValues } = formProps;
  const [sp] = useSearchParams();
  const ticketId = sp.get('ticket') ? Number(sp.get('ticket')) : undefined;
  const initialValuesModified = useMemo(
    () =>
      initialValues
        ? {
            ...initialValues,
            company: initialValues?.company?.id,
            shipper: initialValues?.shipper?.id,
            receiver_localidad: initialValues?.receiver_localidad?.id,
            receiver_provincia: initialValues?.receiver_localidad?.provincia?.id,
            receiver_pais: initialValues?.receiver_localidad?.provincia?.pais?.id,
            ticket: edit ? initialValues?.ticket : ticketId ? ticketId : initialValues?.ticket?.id,
          }
        : ticketId
          ? {
              ticket: ticketId,
              receiver_pais: isBrasilSelected ? COUNTRY.BRASIL : COUNTRY.ARGENTINA,
            }
          : { receiver_pais: isBrasilSelected ? COUNTRY.BRASIL : COUNTRY.ARGENTINA },
    [edit, initialValues, isBrasilSelected, ticketId],
  );
  const {
    initialValuesSetted,
    companySelectProps,
    shippersSelectProps,
    ticketsSelectProps,
    movilLink,
    // contactos para envio de presupuesto
    companyContactData,
    loadingCompanyContactData,
    sendToClient,
    setSendToClient,
  } = useReceptionForm(formProps, initialValuesModified, edit);

  return (
    <Form
      {...formProps}
      onFinish={values => {
        const companyWarn = formProps.form?.getFieldWarning('company') || [];
        const warnings: { companyId: number | string; label: string }[] = [];

        if (companyWarn?.length > 0) {
          warnings.push({
            companyId: values.company,
            label: t('Empresa o Técnico que enviará el paquete'),
          });
        }

        // Mejora: showContactWarningModal centraliza el Modal.error de contacto faltante
        // que antes estaba duplicado manualmente en ShipmentForm y ReceptionForm
        if (showContactWarningModal(warnings, t)) {
          return;
        }

        if (formProps?.onFinish) {
          formProps.onFinish({ ...values, status: PACKAGE_STATES.PENDIENTE });
        }
      }}
      layout="vertical"
      className="ReceptionForm"
      initialValues={initialValuesModified}
      // Mejora: handler genérico que reemplaza el bloque onFinishFailed duplicado en 4 formularios
      onFinishFailed={createFinishFailedHandler(t)}>
      {/* Mejora: FormRecordHeader reemplaza el bloque manual de ID + estado + fechas */}
      {edit && (
        <FormRecordHeader
          id={initialValues?.id}
          label={t('Id de recepción')}
          statusTag={
            initialValues?.status != undefined ? (
              <PackageStateTag packageStatus={initialValues?.status} />
            ) : undefined
          }
          extraTags={
            initialValues?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE ? (
              <Tag color="geekblue">{t('Automática')}</Tag>
            ) : undefined
          }
          createdAt={initialValues?.created_at}
          updatedAt={initialValues?.updated_at}
          className="FormRecordHeader"
        />
      )}

      {/* Mejora: FormSection reemplaza el patrón repetido <Card><div className="title">...</div>...</Card> */}
      <FormSection className="ReceptionFormSection">
        <Form.Item
          label={<b>{t('Ticket')}</b>}
          name={'ticket'}
          rules={[
            {
              required: true,
            },
          ]}>
          <AntdSelect {...ticketsSelectProps} allowClear showSearch />
        </Form.Item>
      </FormSection>
      <FormSection title={t('Detalles de la recepción')} className="ReceptionFormSection">
        <ReceptionDetailForm
          formProps={formProps}
          companySelectProps={companySelectProps}
          shippersSelectProps={shippersSelectProps}
        />
      </FormSection>
      {(!edit ||
        (initialValues?.id &&
          initialValues?.tipo != RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE)) && (
        <>
          <FormSection title={t('Envío de presupuesto')} className="ReceptionFormSection">
            <ReceptionContactForm
              formProps={formProps}
              companyContactData={companyContactData}
              loadingCompanyContactData={loadingCompanyContactData}
              sendToClient={sendToClient}
              setSendToClient={setSendToClient}
            />
          </FormSection>
          <FormSection title={t('Dirección de devolución')} className="ReceptionFormSection">
            <LogisticAddressFormSection
              formProps={formProps}
              initialValuesModified={initialValuesModified}
              setSaveAddress={setSaveAddress}
              initialValuesSetted={initialValuesSetted}
              rowClassName="ReceptionFormRow"
              afterTableFields={
                <Form.Item
                  label={t('Operador logístico de retorno')}
                  name={'return_shipper'}
                  rules={[
                    {
                      required: true,
                      message: t('El operador logístico es requerido'),
                    },
                  ]}>
                  <AntdSelect {...shippersSelectProps} allowClear showSearch />
                </Form.Item>
              }
            />
          </FormSection>
        </>
      )}
      {edit && (
        <FormSection title={t('Logística')} className="ReceptionFormSection">
          <ReceptionLogisticForm />
        </FormSection>
      )}

      <div className="ReceptionFormTitle">{t('Elementos')}</div>
      <PackageItemsList
        packageItems={packageItems}
        setPackageItems={setPackageItems}
        package={{ id: initialValues?.id, movil_link: movilLink }}
        editMode={
          initialValues?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE
            ? 'partial'
            : 'enabled'
        }
      />
      {initialValues?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE && (
        <Alert
          style={{ marginTop: 16 }}
          showIcon
          type="info"
          message={t(
            'No se pueden modificar los elementos de una recepción creada automáticamente',
          )}
        />
      )}
    </Form>
  );
};

export default ReceptionForm;
